import { useAtomValue } from 'jotai';
import { useTheme } from 'styled-components';
import { pitchMarkerColouringModeAtom } from '@/atoms/visualisation';
import { BLOCKED_OUTCOME } from '@/consts/visualisations';
import { useShotMarkerColours } from '@/hooks/shotMap/useShotMarkerColours';
import { ArrowEvent, MarkerEvent } from '@/types/event';
import { ShotMapEventMarker } from '@/types/visualisation';
import { isShotEvent } from '@/utils/visualisations';
import { useEventMarkerColour } from './useEventMarkerColour';

export const useEventMapColouring = (currentlySelectedVideoId: string | null, useDefaultColor?: boolean) => {
  const theme = useTheme();
  const { getShotMarkerFill, getShotMarkerStroke } = useShotMarkerColours();
  const getEventMarkerColour = useEventMarkerColour(currentlySelectedVideoId);
  const colouringMode = useAtomValue(pitchMarkerColouringModeAtom);

  const isNoneColouring = colouringMode === 'none';

  const getShotColours = (shot: ShotMapEventMarker & Pick<MarkerEvent, 'videoEventId'>) => {
    if (useDefaultColor) {
      return { fill: undefined, stroke: undefined };
    }

    if (isNoneColouring) {
      return {
        fill: getEventMarkerColour(shot.videoEventId || shot.eventId, 'neutral', shot.xg, shot.obv),
        stroke: undefined,
      };
    }

    return {
      fill: getShotMarkerFill(shot),
      stroke:
        shot.outcome === BLOCKED_OUTCOME ? getShotMarkerStroke(shot) : theme.colours.visualisations.dataObjects.inverse,
    };
  };

  const getEventColour = (event: MarkerEvent | ArrowEvent) => {
    if (useDefaultColor) {
      return undefined;
    }

    if (colouringMode === 'default' && isShotEvent(event)) {
      return getShotMarkerFill(event);
    }

    const { eventId, videoEventId, successFail, xg, obv } = event;
    return getEventMarkerColour(videoEventId || eventId, successFail, xg, obv);
  };

  return { getShotColours, getEventColour };
};
